/**
 * HomeworkPage — `/homework`
 *
 * Homework feed from followed teachers (GET /api/homework/), with a
 * self-report completion toggle (POST/DELETE /api/homework/:id/complete/).
 * Mirrors the mobile app's homework list (mobile/lib/features/subjects).
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { listHomework, completeHomework, uncompleteHomework } from '@/api/social'
import { SectionHeader, Hex } from '@/components/kit'
import { BookCheck, Calendar, CheckCircle2, Circle, ListChecks } from 'lucide-react'

function fmtDue(dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
}

function isOverdue(dateStr, isCompleted) {
  if (!dateStr || isCompleted) return false
  return new Date(dateStr).getTime() < Date.now()
}

export default function HomeworkPage() {
  const { t } = useTranslation()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [pending, setPending] = useState(() => new Set())

  async function load(config) {
    try {
      const { data } = await listHomework(config)
      setItems(data.results ?? data ?? [])
    } catch (err) {
      if (err.code === 'ERR_CANCELED') return
    } finally {
      if (!config?.signal?.aborted) setLoading(false)
    }
  }

  useEffect(() => {
    const controller = new AbortController()
    load({ signal: controller.signal })
    return () => controller.abort()
  }, [])

  async function toggle(hw) {
    setPending((s) => new Set(s).add(hw.id))
    setItems((list) => list.map((x) => (x.id === hw.id ? { ...x, is_completed: !x.is_completed } : x)))
    try {
      if (hw.is_completed) await uncompleteHomework(hw.id)
      else await completeHomework(hw.id)
    } catch {
      load() // revert on failure
    } finally {
      setPending((s) => { const n = new Set(s); n.delete(hw.id); return n })
    }
  }

  const doneCount = items.filter((x) => x.is_completed).length

  return (
    <div>
      <SectionHeader
        title={t('homework.title', 'Vazifalar')}
        subtitle={t('homework.subtitle', "O'qituvchilaringiz tomonidan berilgan vazifalar")}
        right={
          items.length > 0 && (
            <div className="t-xs t-mut fw-6">
              {t('homework.progress', '{{done}}/{{total}} bajarildi', { done: doneCount, total: items.length })}
            </div>
          )
        }
      />

      {loading && (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--coral-soft)', borderTopColor: 'var(--coral)' }} />
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="card" style={{ padding: 32, borderRadius: 22, textAlign: 'center', borderStyle: 'dashed', boxShadow: 'none' }}>
          <ListChecks size={36} strokeWidth={1.5} style={{ margin: '0 auto 10px', color: 'var(--muted)' }} />
          <div className="t-sm t-mut">{t('homework.empty', "Hali vazifalar yo'q")}</div>
        </div>
      )}

      <div className="flex flex-col gap-2.5">
        {items.map((hw) => {
          const overdue = isOverdue(hw.due_date, hw.is_completed)
          const busy = pending.has(hw.id)
          return (
            <div key={hw.id} className="card" style={{ padding: 18, borderRadius: 22 }}>
              <div className="row gap-12" style={{ alignItems: 'flex-start' }}>
                <Hex
                  size="sm"
                  icon={BookCheck}
                  bg={hw.is_completed ? 'var(--mint-soft)' : 'var(--coral-soft)'}
                  fg={hw.is_completed ? 'var(--mint)' : 'var(--coral)'}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="fw-7 t-sm">{hw.title}</div>
                  <div className="t-xs t-mut" style={{ marginTop: 2 }}>
                    {hw.subject_name && <>{hw.subject_name} · </>}
                    {hw.teacher?.full_name || hw.teacher?.username}
                  </div>
                  {hw.description && (
                    <div className="t-sm" style={{ marginTop: 8, color: 'var(--ink-2)' }}>{hw.description}</div>
                  )}
                  <div className="row gap-6" style={{ marginTop: 10 }}>
                    <Calendar size={12} style={{ color: overdue ? 'var(--danger)' : 'var(--ink-3)' }} />
                    <span className="t-xs fw-6" style={{ color: overdue ? 'var(--danger)' : 'var(--ink-3)' }}>
                      {t('homework.dueDate', 'Muddat')}: {fmtDue(hw.due_date)}
                      {overdue && ` · ${t('homework.overdue', 'muddati o\'tgan')}`}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => toggle(hw)}
                  disabled={busy}
                  className={'btn ' + (hw.is_completed ? 'btn-soft' : 'btn-primary')}
                  style={{ flexShrink: 0 }}
                >
                  {hw.is_completed ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                  {hw.is_completed ? t('homework.markUndone', 'Bekor qilish') : t('homework.markDone', 'Bajarildi')}
                </button>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
